import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  PencilSquareIcon,
  ShieldExclamationIcon,
  TrashIcon,
  KeyIcon,
  ArrowRightStartOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { Loader } from '@mantine/core';
import { apiService } from '../services/api';
import InlineFeedback from '../components/InlineFeedback';
import { useFeedback } from '../hooks/useFeedback';
import ApiKeysTab from '../components/converter/ApiKeysTab';
import BillingTab from '../components/converter/BillingTab';
import ConfirmDialog from '../components/ConfirmDialog';
import { useLayoutActions } from '../components/layout/LayoutActionsContext';
import { notifyInstanceInfoChanged } from '../utils/instanceEvents';
import { urlEnum, useUrlState } from '../hooks/useUrlState';

type SettingsTab = 'account' | 'api-keys' | 'billing';

const tabs: { id: SettingsTab; label: string }[] = [
  { id: 'account', label: 'Account' },
  { id: 'api-keys', label: 'API Keys' },
  { id: 'billing', label: 'Billing' },
];

const URL_SPEC = {
  tab: urlEnum<SettingsTab>(['account', 'api-keys', 'billing'], 'account'),
};

function Settings() {
  const layoutActions = useLayoutActions();
  const [instanceInfo, setInstanceInfo] = useState(apiService.getInstanceInfo());
  const [state, setUrlState] = useUrlState(URL_SPEC);
  const switchTab = (tab: SettingsTab) => setUrlState({ tab });
  const { data: instance, isFetching: instanceFetching } = useQuery({
    queryKey: ['instance', instanceInfo?.instanceKey],
    queryFn: apiService.getInstance,
    enabled: !!instanceInfo,
    retry: false,
  });
  const billingAllowed = instance?.instance_mode === 'persistent' && instance.tier !== 'free';
  // Show nothing for the billing URL while the instance query runs and has not allowed billing yet.
  const activeTab = state.tab !== 'billing' || billingAllowed ? state.tab : instanceFetching ? null : 'account';

  useEffect(() => {
    if (state.tab === 'billing' && !instanceFetching && !billingAllowed) {
      setUrlState({ tab: 'account' });
    }
  }, [state.tab, instanceFetching, billingAllowed, setUrlState]);

  // Save form (temp instances)
  const [saveEmail, setSaveEmail] = useState(instanceInfo?.email || '');
  const [isSaving, setIsSaving] = useState(false);
  const saveFeedback = useFeedback();

  // Instance rename
  const [renameValue, setRenameValue] = useState(instanceInfo?.name || '');
  const [isRenaming, setIsRenaming] = useState(false);
  const renameFeedback = useFeedback();

  // Email update (persistent instances)
  const [updateEmail, setUpdateEmail] = useState(instanceInfo?.email || '');
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const emailFeedback = useFeedback();

  const [isSigningOutOthers, setIsSigningOutOthers] = useState(false);
  const [showSignOutOthersConfirm, setShowSignOutOthersConfirm] = useState(false);
  const [signOutOthersError, setSignOutOthersError] = useState<string | null>(null);
  const signOutOthersFeedback = useFeedback();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const refreshInstanceInfo = () => {
    setInstanceInfo(apiService.getInstanceInfo());
    notifyInstanceInfoChanged();
  };

  const handleSave = async () => {
    if (!saveEmail || !saveEmail.includes('@')) {
      saveFeedback.showError('Enter a valid email address.');
      return;
    }
    setIsSaving(true);
    saveFeedback.clear();
    try {
      const result = await apiService.saveInstance(saveEmail);
      saveFeedback.showSuccess(`We sent a confirmation link to ${result.pending_email}. Open it in this browser to finish saving.`);
    } catch (error: any) {
      saveFeedback.showError(error.response?.data?.message || 'Failed to save instance.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRename = async () => {
    const name = renameValue.trim();
    if (!name) {
      renameFeedback.showError('Enter an instance name.');
      return;
    }
    if (name === instanceInfo?.name) {
      return;
    }
    setIsRenaming(true);
    try {
      const result = await apiService.renameInstance(name);
      refreshInstanceInfo();
      setRenameValue(result.name);
      renameFeedback.showSuccess('Instance renamed.');
    } catch (error: any) {
      renameFeedback.showError(error.response?.data?.message || 'Failed to rename instance.');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!updateEmail || !updateEmail.includes('@')) {
      emailFeedback.showError('Enter a valid email address.');
      return;
    }
    setIsUpdatingEmail(true);
    try {
      const result = await apiService.saveInstance(updateEmail);
      emailFeedback.showSuccess(`We sent a confirmation link to ${result.pending_email}. Open it to change your sign-in email. This signs out all other sessions.`);
    } catch (error: any) {
      emailFeedback.showError(error.response?.data?.message || 'Failed to update email.');
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleSignOutOthers = async () => {
    setIsSigningOutOthers(true);
    setSignOutOthersError(null);
    try {
      const result = await apiService.signOutOtherSessions();
      signOutOthersFeedback.showSuccess(
        `Signed out ${result.signed_out_count} other ${result.signed_out_count === 1 ? 'session' : 'sessions'}.`,
      );
      setShowSignOutOthersConfirm(false);
    } catch (error: any) {
      setSignOutOthersError(error.response?.data?.message || 'Failed to sign out other sessions.');
    } finally {
      setIsSigningOutOthers(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await apiService.deleteInstance();
    } catch (error: any) {
      setDeleteError(error.response?.data?.message || 'Failed to delete instance.');
      setIsDeleting(false);
      return;
    }
    apiService.clearRegistration();
    window.location.href = '/';
  };

  if (!instanceInfo) {
    return <div className="text-secondary">No active instance.</div>;
  }

  const isPersistent = instanceInfo.instanceMode === 'persistent';

  return (
    <div>
      <h1 className="text-2xl font-bold text-heading mb-6">Settings</h1>

      <div className="border-b border-gray-700 mb-6">
        <nav className="flex space-x-8" role="tablist">
          {tabs.filter((tab) => tab.id !== 'billing' || billingAllowed).map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => switchTab(tab.id)}
              className={`pb-3 px-1 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-secondary hover:text-body-color'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'account' && (
        <div className="bg-widget-bg rounded-lg border border-gray-700 p-6 max-w-2xl">
          <div className="mb-4 space-y-1">
            <p className="text-sm text-body-color">
              <span className="text-secondary">Instance ID:</span>{' '}
              <span className="text-heading font-mono">{instanceInfo.instanceKey}</span>
            </p>
            {!isPersistent && (
              <p className="text-sm text-body-color">
                <span className="text-secondary">Status:</span>{' '}
                <span className="bg-warning/20 text-warning px-2 py-0.5 rounded text-xs font-medium">
                  Unsaved (this browser only)
                </span>
              </p>
            )}
          </div>

          {/* Instance name */}
          <div className="border-t border-secondary pt-4 mb-4">
            <h3 className="text-heading font-medium mb-2 flex items-center gap-2">
              <PencilSquareIcon className="w-4 h-4" />
              Instance Name
            </h3>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                handleRename();
              }}
            >
              <input
                type="text"
                aria-label="Instance name"
                placeholder="My Instance"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="flex-1 px-3 py-2 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary text-sm"
              />
              <button
                type="submit"
                aria-label="Save instance name"
                disabled={isRenaming || !renameValue.trim() || renameValue.trim() === instanceInfo.name}
                className="btn-secondary text-sm w-24 flex items-center justify-center gap-1"
              >
                {isRenaming ? <Loader size="xs" /> : 'Save'}
              </button>
            </form>
            <InlineFeedback feedback={renameFeedback.feedback} className="mt-2" />
          </div>

          {!isPersistent ? (
            <div className="border-t border-secondary pt-4">
              <h3 className="text-heading font-medium mb-2 flex items-center gap-2">
                <EnvelopeIcon className="w-5 h-5" />
                Save your instance
              </h3>
              <p className="text-sm text-body-color/70 mb-4">
                This instance is unsaved. Only this browser can open it. Save it with your email. We send a confirmation link. Open it to finish. Then sign in from any device and manage API keys.
              </p>
              <form
                aria-label="Save instance"
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                <input
                  type="email"
                  required
                  aria-label="Email address"
                  placeholder="Email address"
                  value={saveEmail}
                  onChange={(e) => setSaveEmail(e.target.value)}
                  className="w-full px-3 py-2 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary"
                />
                <button
                  type="submit"
                  disabled={isSaving || !saveEmail.trim()}
                  className="w-full btn-primary-full py-2 flex items-center justify-center gap-2"
                >
                  {isSaving ? (
                    <>
                      <Loader size="xs" color="white" />
                      Saving...
                    </>
                  ) : (
                    'Save Instance'
                  )}
                </button>
                <InlineFeedback feedback={saveFeedback.feedback} />
              </form>
              <p className="text-sm text-body-color/70 mt-3">
                Already saved an instance?{' '}
                <Link to="/signin" className="text-primary hover:text-primary-hover transition-colors">
                  Sign in
                </Link>
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Sign-in email */}
              <div>
                <h3 className="text-heading font-medium mb-2 flex items-center gap-2">
                  <EnvelopeIcon className="w-4 h-4" />
                  Sign-in Email
                </h3>
                <p className="text-xs text-body-color/50 mb-2">
                  We send sign-in links for this instance to this address. To change it, we send a confirmation link to the new address. The change applies when you open it. This signs out all other sessions.
                </p>
                <form
                  aria-label="Update sign-in email"
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleUpdateEmail();
                  }}
                >
                  <input
                    type="email"
                    required
                    aria-label="Sign-in email"
                    placeholder={instanceInfo.email || 'Email address'}
                    value={updateEmail}
                    onChange={(e) => setUpdateEmail(e.target.value)}
                    className="flex-1 px-3 py-2 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary text-sm"
                  />
                  <button
                    type="submit"
                    aria-label="Save sign-in email"
                    disabled={isUpdatingEmail || !updateEmail.trim() || updateEmail.trim() === instanceInfo.email}
                    className="btn-secondary text-sm w-24 flex items-center justify-center gap-1"
                  >
                    {isUpdatingEmail ? <Loader size="xs" /> : 'Save'}
                  </button>
                </form>
                <InlineFeedback feedback={emailFeedback.feedback} className="mt-2" />
              </div>

              {/* Security */}
              <div className="border-t border-secondary pt-4">
                <h3 className="text-heading font-medium mb-2 flex items-center gap-2">
                  <ShieldExclamationIcon className="w-4 h-4" />
                  Security
                </h3>
                <button
                  onClick={() => setShowSignOutOthersConfirm(true)}
                  disabled={isSigningOutOthers}
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  {isSigningOutOthers && <Loader size="xs" />}
                  Sign out other sessions
                </button>
                <InlineFeedback feedback={signOutOthersFeedback.feedback} className="mt-2" />
                <p className="text-xs text-body-color/50 mt-2">
                  This signs you out of this instance in every other browser. You stay signed in here.
                </p>
              </div>
            </div>
          )}

          {/* Sign out / delete instance */}
          <div className="border-t border-secondary pt-4 mt-4">
            <div className="flex items-center justify-between">
              {layoutActions && (
                <button
                  onClick={layoutActions.openExitDialog}
                  className="text-body-color hover:text-heading text-sm flex items-center gap-2 py-2 transition-colors"
                >
                  <ArrowRightStartOnRectangleIcon className="w-4 h-4" />
                  {isPersistent ? 'Sign out' : 'Exit'}
                </button>
              )}
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-500 hover:text-red-400 text-sm flex items-center gap-2 py-2"
              >
                <TrashIcon className="w-4 h-4" />
                Delete Instance
              </button>
            </div>
          </div>

          <ConfirmDialog
            open={showDeleteConfirm}
            title="Delete Instance"
            message="This deletes your instance and all its data. You cannot undo it."
            confirmLabel="Delete Instance"
            danger
            pending={isDeleting}
            error={deleteError}
            onConfirm={handleDelete}
            onCancel={() => {
              setShowDeleteConfirm(false);
              setDeleteError(null);
            }}
          />

          <ConfirmDialog
            open={showSignOutOthersConfirm}
            title="Sign out other sessions"
            message="This signs you out of this instance in every other browser. You stay signed in here."
            confirmLabel="Sign out others"
            pending={isSigningOutOthers}
            error={signOutOthersError}
            onConfirm={handleSignOutOthers}
            onCancel={() => {
              setShowSignOutOthersConfirm(false);
              setSignOutOthersError(null);
            }}
          />
        </div>
      )}

      {activeTab === 'billing' && <BillingTab />}

      {activeTab === 'api-keys' &&
        (isPersistent ? (
          <ApiKeysTab />
        ) : (
          <div className="bg-widget-bg rounded-lg border border-gray-700 p-6 w-fit max-w-2xl">
            <p className="text-sm text-secondary flex items-center gap-2 mb-4">
              <KeyIcon className="w-5 h-5" />
              API keys are available for saved instances. Save your instance to manage API keys.
            </p>
            <button onClick={() => switchTab('account')} className="btn-secondary text-sm">
              Go to Account
            </button>
          </div>
        ))}
    </div>
  );
}

export default Settings;
